import React, { useEffect, useState } from "react";
import { Alert, Input, Button } from "reactstrap";
import StepCompleteActionButton from "./StepCompleteActionButton";

const SsnFlow = ({
    preview,
    email,
    rawText,
    onSubmit,
    onRetake
}) => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [fields, setFields] = useState({
        ssnDigits: "",
        ssnFormatted: "",
        ssnRestriction: "",
        ssnIssuingAuthority: "SSA"
    });

    const updateField = (k, v) =>
        setFields(prev => ({ ...prev, [k]: v }));

    // -------------------------------------
    // OCR PARSER FOR SSN
    // -------------------------------------
    const parseSSN = (raw) => {
        const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);

        // SSN detection
        const match =
            raw.match(/\b\d{3}[- ]?\d{2}[- ]?\d{4}\b/) ||
            raw.match(/\b\d{3}[- ]?\d{5}\b/);

        let digits = "";
        if (match) {
            digits = match[0].replace(/\D/g, "").slice(0, 9);
        }

        let formatted = "";
        if (digits.length === 9) {
            formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
        }

        // Restriction extraction
        let restriction = "";
        if (digits.length === 9) {
            const idx = lines.findIndex(
                l => l.includes(formatted) || l.includes(digits)
            );

            if (idx > 0) {
                const before = lines.slice(0, idx).join(" ").toUpperCase();
                if (before.includes("NOT VALID") || before.includes("VALID FOR")) {
                    restriction = lines.slice(0, idx).join(" ");
                }
            }
        }

        setFields({
            ssnDigits: digits,
            ssnFormatted: formatted,
            ssnRestriction: restriction,
            ssnIssuingAuthority: "SSA"
        });
    };

    useEffect(() => {
        if (rawText) parseSSN(rawText);
    }, [rawText]);

    // -------------------------------------
    // FINAL SUBMIT
    // -------------------------------------
    const submitFinal = async () => {
        setError("");
        setLoading(true);

        if (fields.ssnDigits.length !== 9) {
            setError("Invalid SSN.");
            setLoading(false);
            return;
        }

        const payload = {
            type: "ssn",
            data: {
                email,
                ssn: fields.ssnDigits,
                ssn_restriction_text: fields.ssnRestriction,
                ssn_issuing_authority: fields.ssnIssuingAuthority
            }
        };

        try {
            const resp = await onSubmit(payload);
            if (!resp || resp.code !== 1) {
                setError(resp?.message || "Save failed");
                setLoading(false);
                return;
            }
        } catch (err) {
            setError("Unexpected error");
            setLoading(false);
        }
    };

    // -------------------------------------
    // UI
    // -------------------------------------
    return (
        <>
            {error && <Alert color="danger">{error}</Alert>}

            <h1 className="text-dark mb-3 fw-semibold">Verify SSN</h1>

            {preview && (
                <img
                    src={preview}
                    alt="ssn"
                    style={{
                        width: "100%",
                        borderRadius: 8,
                        marginBottom: 20
                    }}
                />
            )}

            <label>SSN</label>
            <Input
                className="mb-3"
                value={fields.ssnFormatted}
                onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "").slice(0, 9);
                    updateField("ssnDigits", raw);
                    updateField(
                        "ssnFormatted",
                        raw.length === 9
                            ? `${raw.slice(0, 3)}-${raw.slice(3, 5)}-${raw.slice(5)}`
                            : e.target.value
                    );
                }}
            />

            <label>Restriction</label>
            <Input
                className="mb-3"
                value={fields.ssnRestriction}
                onChange={(e) => updateField("ssnRestriction", e.target.value)}
            />

            <label>Issuing Authority</label>
            <Input
                className="mb-4"
                value={fields.ssnIssuingAuthority}
                onChange={(e) => updateField("ssnIssuingAuthority", e.target.value)}
            />

            <StepCompleteActionButton
                label={loading ? "SAVING..." : "SUBMIT"}
                onClick={submitFinal}
                disabled={loading}
            />

            <Button className="w-100 mt-2" color="secondary" onClick={onRetake}>
                RETAKE PICTURE
            </Button>
        </>
    );
};

export default SsnFlow;